'use client';

import { Check, ChevronDown, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../utils/utils';
import { Button } from '../Button/Button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command/Command';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover';

export interface ComboBoxOption {
    value: string;
    label: string;
}

export interface ComboBoxProps {
    options: ComboBoxOption[];
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    groupLabel?: string;
    value?: string[];
    onChange?: (value: string[]) => void;
    className?: string;
    minSelections?: number;
    maxSelections?: number;
}

export function ComboBox({
    options,
    placeholder = 'Seleccionar opción',
    searchPlaceholder = 'Buscar...',
    emptyMessage = 'No se encontró ninguna opción.',
    groupLabel = 'seleccionadas',
    value: controlledValue,
    onChange,
    className,
    minSelections = 0,
    maxSelections,
}: ComboBoxProps) {
    const [open, setOpen] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState<string[]>([]);

    const value = controlledValue !== undefined ? controlledValue : internalValue;
    const setValue = (newValue: string[]) => {
        if (controlledValue === undefined) {
            setInternalValue(newValue);
        }
        onChange?.(newValue);
    };

    const toggleValue = (optionValue: string) => {
        const isSelected = value.includes(optionValue);

        if (isSelected && minSelections > 0 && value.length <= minSelections) {
            return;
        }

        if (!isSelected && maxSelections !== undefined && value.length >= maxSelections) {
            return;
        }

        const newValue = isSelected ? value.filter((v) => v !== optionValue) : [...value, optionValue];
        setValue(newValue);
    };

    const resetFilter = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (minSelections > 0) {
            return;
        }
        setValue([]);
    };

    const getDisplayText = () => {
        if (value.length === 0) return placeholder;
        if (value.length === 1) {
            return options.find((option) => option.value === value[0])?.label;
        }
        return `${value.length} ${groupLabel}`;
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="default"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-full justify-between font-normal font-roboto border-gray-qm-100 hover:!bg-gray-50/30 hover:!text-foreground',
                        className,
                    )}
                >
                    {getDisplayText()}
                    {value.length > 0 && minSelections === 0 ? (
                        <X className="h-4 w-4 opacity-50 hover:opacity-100" onClick={resetFilter} />
                    ) : (
                        <ChevronDown className="opacity-50" />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0 border-gray-qm-100"
                align="start"
                side="bottom"
                sideOffset={4}
                collisionPadding={8}
                avoidCollisions={true}
            >
                <Command>
                    <CommandInput placeholder={searchPlaceholder} className="h-9" />
                    <CommandList>
                        <CommandEmpty>{emptyMessage}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = value.includes(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        value={option.value}
                                        onSelect={() => {
                                            toggleValue(option.value);
                                        }}
                                        className={cn('pl-8', isSelected && 'bg-primary/10')}
                                    >
                                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                            <Check
                                                className={cn(
                                                    'h-4 w-4',
                                                    isSelected ? 'opacity-100 text-primary' : 'opacity-0',
                                                )}
                                            />
                                        </span>
                                        {option.label}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

ComboBox.displayName = 'ComboBox';

export default ComboBox;
